import React, { createContext, useContext, useState, useEffect } from 'react';
import { CognitoUserPool, CognitoUser, AuthenticationDetails, CognitoUserSession } from 'amazon-cognito-identity-js';
import { config } from '../config';

interface AuthContextType {
  isAuthenticated: boolean;
  user: CognitoUser | null;
  session: CognitoUserSession | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  getIdToken: () => Promise<string | null>;
  getAccessToken: () => Promise<string | null>;
  resetPassword: (email: string) => Promise<void>;
  confirmPasswordReset: (email: string, confirmationCode: string, newPassword: string) => Promise<void>;
  completeNewPasswordChallenge: (newPassword: string, requiredAttributes?: any) => Promise<void>;
  newPasswordRequired: boolean;
  tempUser: CognitoUser | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<CognitoUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<CognitoUserSession | null>(null);
  const [newPasswordRequired, setNewPasswordRequired] = useState(false);
  const [tempUser, setTempUser] = useState<CognitoUser | null>(null);

  const userPool = new CognitoUserPool({
    UserPoolId: config.COGNITO_USER_POOL_ID,
    ClientId: config.COGNITO_CLIENT_ID,
  });

  useEffect(() => {
    // Check for existing session
    const currentUser = userPool.getCurrentUser();
    if (currentUser) {
      currentUser.getSession((err: any, session: CognitoUserSession | null) => {
        if (!err && session && session.isValid()) {
          setUser(currentUser);
          setSession(session);
        }
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, []);

  const signIn = async (email: string, password: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: email,
        Password: password,
      });

      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      });

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (session) => {
          setUser(cognitoUser);
          setSession(session);
          setNewPasswordRequired(false);
          setTempUser(null);
          resolve();
        },
        onFailure: (err) => {
          reject(err);
        },
        newPasswordRequired: (userAttributes) => {
          console.log('New password required for user:', email);
          console.log('User attributes:', userAttributes);
          setTempUser(cognitoUser);
          setNewPasswordRequired(true);
          resolve(); // Resolve here so the UI can handle the new password flow
        },
      });
    });
  };

  const signOut = async (): Promise<void> => {
    if (user) {
      user.signOut();
    }
    setUser(null);
    setSession(null);
  };

  const getIdToken = async (): Promise<string | null> => {
    if (!session || !session.isValid()) {
      // Try to refresh the session
      if (user) {
        return new Promise((resolve) => {
          user.getSession((err: any, newSession: CognitoUserSession | null) => {
            if (!err && newSession && newSession.isValid()) {
              setSession(newSession);
              resolve(newSession.getIdToken().getJwtToken());
            } else {
              resolve(null);
            }
          });
        });
      }
      return null;
    }
    return session.getIdToken().getJwtToken();
  };

  const getAccessToken = async (): Promise<string | null> => {
    if (!session || !session.isValid()) {
      // Try to refresh the session
      if (user) {
        return new Promise((resolve) => {
          user.getSession((err: any, newSession: CognitoUserSession | null) => {
            if (!err && newSession && newSession.isValid()) {
              setSession(newSession);
              resolve(newSession.getAccessToken().getJwtToken());
            } else {
              resolve(null);
            }
          });
        });
      }
      return null;
    }
    return session.getAccessToken().getJwtToken();
  };

  const resetPassword = async (email: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      console.log('Attempting password reset for:', email);
      console.log('User Pool ID:', config.COGNITO_USER_POOL_ID);
      console.log('Client ID:', config.COGNITO_CLIENT_ID);
      
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      });

      cognitoUser.forgotPassword({
        onSuccess: (data) => {
          console.log('Password reset email sent successfully:', data);
          console.log('Reset data:', JSON.stringify(data, null, 2));
          resolve();
        },
        onFailure: (err) => {
          console.error('Password reset failed:', err);
          console.error('Error details:', JSON.stringify(err, null, 2));
          reject(err);
        },
        inputVerificationCode: (data) => {
          console.log('Verification code input requested:', data);
          // This callback is called when the system is ready to accept verification code
          // In some cases, this might be called instead of onSuccess
          // Let's resolve here too to handle both cases
          resolve();
        }
      });
    });
  };

  const confirmPasswordReset = async (email: string, confirmationCode: string, newPassword: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      });

      cognitoUser.confirmPassword(confirmationCode, newPassword, {
        onSuccess: () => {
          resolve();
        },
        onFailure: (err) => {
          reject(err);
        },
      });
    });
  };

  const completeNewPasswordChallenge = async (newPassword: string, requiredAttributes: any = {}): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!tempUser) {
        reject(new Error('No user session available for password change'));
        return;
      }

      console.log('Completing new password challenge with:', { newPassword: '[REDACTED]', requiredAttributes });

      tempUser.completeNewPasswordChallenge(newPassword, requiredAttributes, {
        onSuccess: (session) => {
          console.log('New password challenge completed successfully');
          setUser(tempUser);
          setSession(session);
          setNewPasswordRequired(false);
          setTempUser(null);
          resolve();
        },
        onFailure: (err) => {
          console.error('New password challenge failed:', err);
          reject(err);
        },
      });
    });
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!user && !!session && session.isValid(),
        user,
        session,
        loading,
        signIn,
        signOut,
        getIdToken,
        getAccessToken,
        resetPassword,
        confirmPasswordReset,
        completeNewPasswordChallenge,
        newPasswordRequired,
        tempUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}